import { useState, useEffect } from 'react';
import supabase from './supabaseClient';
import { TelegramService } from './services/telegramService';
import { ReminderService } from './services/reminderService';
import AdvancedMealAnalysis from './components/analytics/AdvancedMealAnalysis';
import { PullToRefresh } from './components/ui/PullToRefresh';
import { calculateLoggingStreak, type MealRow } from './utils/clientStatsUtils';

interface Insight {
  type: 'success' | 'warning' | 'action' | 'trend';
  title: string;
  description: string;
  action?: string;
  priority: 'high' | 'medium' | 'low';
  icon: string;
}

interface GoalPerformanceInsightsProps {
  selectedUserId: string;
  clientName: string;
  onSwitchToProfile?: () => void;
  onSwitchToGoals?: () => void;
  onUpgrade?: () => void;
}

export default function GoalPerformanceInsights({ selectedUserId, clientName, onSwitchToProfile, onSwitchToGoals, onUpgrade }: GoalPerformanceInsightsProps) {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [coachNotes, setCoachNotes] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [activeTab, setActiveTab] = useState<'insights' | 'meal-analysis'>('insights');

  useEffect(() => {
    if (selectedUserId) {
      generateInsights();
      loadCoachNotes();
    }
  }, [selectedUserId]);

  const generateInsights = async () => {
    try {
      setLoading(true);
      console.log('🔍 Generating insights for user:', selectedUserId);
      
      // Get client's recent meals
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: meals, error: mealsError } = await supabase
        .from('Mastersheet')
        .select('*')
        .eq('telegram_user_id', selectedUserId)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      console.log('📊 Meals fetched:', meals?.length || 0, 'Error:', mealsError);

      if (mealsError) {
        console.error('Error fetching meals:', mealsError);
        return;
      }

      const { data: streakMeals, error: streakError } = await supabase
        .from('Mastersheet')
        .select('meal_time, created_at')
        .eq('telegram_user_id', selectedUserId);

      if (streakError) {
        console.error('Error fetching streak meals:', streakError);
      }

      // Get client's diet goals
      const { data: dietGoals, error: goalsError } = await supabase
        .from('client_diet_goals')
        .select('*')
        .eq('telegram_user_id', selectedUserId)
        .single();

      if (goalsError && goalsError.code !== 'PGRST116') {
        console.error('Error fetching diet goals:', goalsError);
        return;
      }

      const newInsights: Insight[] = [];

      // Analyze patterns and generate insights
      if (meals && meals.length > 0) {
        console.log('🔍 Processing', meals.length, 'meals for insights');
        
        // Calculate current streak
        const currentStreak = calculateLoggingStreak((streakMeals || meals || []) as MealRow[]);
        console.log('🔥 Current streak:', currentStreak);
        
        // Calculate weekly success rate
        const weeklySuccessRate = dietGoals ? calculateWeeklySuccessRate(meals, dietGoals) : 0;
        const adherenceDays = calculateSevenDayAdherence(meals);
        console.log('📊 Weekly success rate:', weeklySuccessRate, 'Diet goals:', !!dietGoals);
        newInsights.push({
          type: 'trend',
          title: '🗓️ Logging Adherence',
          description: `${clientName} logged meals on ${adherenceDays}/7 days in the last week.`,
          priority: 'high',
          icon: '🗓️'
        });
        
        // Analyze meal timing patterns
        const mealTimingInsights = analyzeMealTiming(meals);
        console.log('⏰ Meal timing insights:', mealTimingInsights.length);
        
        // Analyze macro balance
        const macroInsights = dietGoals ? analyzeMacroBalance(meals, dietGoals) : [];
        console.log('🥩 Macro insights:', macroInsights.length);
        
        // Generate streak insights
        if (currentStreak >= 7) {
          console.log('✅ Adding streak insight for', currentStreak, 'days');
          newInsights.push({
            type: 'success',
            title: '🔥 Impressive Streak!',
            description: `${clientName} has logged meals for ${currentStreak} consecutive days. This shows excellent consistency and commitment.`,
            priority: 'high',
            icon: '🔥'
          });
        } else if (currentStreak === 0) {
          console.log('⚠️ Adding no activity insight');
          newInsights.push({
            type: 'warning',
            title: '⚠️ No Recent Activity',
            description: `${clientName} hasn't logged any meals recently. Consider reaching out to check in on their progress.`,
            action: 'Send a motivational message',
            priority: 'high',
            icon: '⚠️'
          });
        } else if (currentStreak >= 4) {
          console.log('🔥 Adding good streak insight');
          newInsights.push({
            type: 'success',
            title: '🔥 Consistent Logging',
            description: `${clientName} has been logging meals consistently for ${currentStreak} days. Great dedication to their nutrition goals!`,
            priority: 'medium',
            icon: '🔥'
          });
        } else {
          console.log('📊 Current streak is', currentStreak, '- no streak insight added');
        }

        // Weekly performance insights
        if (weeklySuccessRate >= 80) {
          newInsights.push({
            type: 'success',
            title: '🎯 Excellent Goal Achievement',
            description: `${clientName} is achieving ${Math.round(weeklySuccessRate)}% of their weekly nutrition goals. They're on track for success!`,
            priority: 'medium',
            icon: '🎯'
          });
        } else if (weeklySuccessRate < 70) {
          newInsights.push({
            type: 'trend',
            title: '📉 Below Target Performance',
            description: `${clientName} is only achieving ${Math.round(weeklySuccessRate)}% of their goals. They might need additional support or goal adjustments.`,
            action: 'Review and adjust goals',
            priority: 'high',
            icon: '📉'
          });
        } else {
          newInsights.push({
            type: 'trend',
            title: '📊 Good Progress',
            description: `${clientName} is achieving ${Math.round(weeklySuccessRate)}% of their weekly nutrition goals. They're making steady progress!`,
            priority: 'medium',
            icon: '📊'
          });
        }

        // Add meal timing insights
        newInsights.push(...mealTimingInsights);
        
        // Add macro balance insights
        newInsights.push(...macroInsights);

        // Check for consistency patterns
        const consistencyInsight = analyzeConsistency(meals);
        if (consistencyInsight) {
          newInsights.push(consistencyInsight);
        }

      } else {
        // No meals logged
        newInsights.push({
          type: 'warning',
          title: '📝 No Meal Data',
          description: `${clientName} hasn't logged any meals yet. Help them get started with their nutrition tracking journey.`,
          action: 'Send onboarding message',
          priority: 'high',
          icon: '📝'
        });
      }

      // Sort insights by priority
      newInsights.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      console.log('💡 Insights generated:', newInsights.length, newInsights);
      setInsights(newInsights);
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateWeeklySuccessRate = (meals: any[], dietGoals: any): number => {
    if (!meals.length) return 0;

    // Get last 7 days and group by date
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const recentMeals = meals.filter(meal => new Date(meal.created_at) >= sevenDaysAgo);

    const mealsByDate = new Map<string, any[]>();
    recentMeals.forEach(meal => {
      const dateKey = new Date(meal.created_at).toISOString().slice(0, 10);
      if (!mealsByDate.has(dateKey)) {
        mealsByDate.set(dateKey, []);
      }
      mealsByDate.get(dateKey)!.push(meal);
    });

    // Calculate success across days WITH meals in the last 7 days
    let totalSuccess = 0;
    let daysWithMeals = 0;

    mealsByDate.forEach((dayMeals, dateKey) => {
      const date = new Date(dateKey);
      if (date < sevenDaysAgo) return;

      const dayCalories = dayMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
      const dayProtein = dayMeals.reduce((sum, meal) => sum + (meal.protein_g || 0), 0);
      const dayCarbs = dayMeals.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0);
      const dayFat = dayMeals.reduce((sum, meal) => sum + (meal.fat_g || 0), 0);

      const caloriesSuccess = dayCalories > 0 ? Math.min(100, (dayCalories / dietGoals.target_calories) * 100) : 0;
      const proteinSuccess = dayProtein > 0 ? Math.min(100, (dayProtein / dietGoals.protein_target) * 100) : 0;
      const carbsSuccess = dayCarbs > 0 ? Math.min(100, (dayCarbs / dietGoals.carbs_target) * 100) : 0;
      const fatSuccess = dayFat > 0 ? Math.min(100, (dayFat / dietGoals.fat_target) * 100) : 0;

      const daySuccess = (caloriesSuccess + proteinSuccess + carbsSuccess + fatSuccess) / 4;
      totalSuccess += daySuccess;
      daysWithMeals++;
    });

    return daysWithMeals > 0 ? totalSuccess / daysWithMeals : 0;
  };

  const calculateSevenDayAdherence = (meals: any[]): number => {
    if (!meals.length) return 0;
    const mealsByDate = new Set(
      meals.map(meal => new Date(meal.created_at).toISOString().slice(0, 10))
    );
    let loggedDays = 0;
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const dateKey = d.toISOString().slice(0, 10);
      if (mealsByDate.has(dateKey)) loggedDays++;
    }
    return loggedDays;
  };

  const analyzeMealTiming = (meals: any[]): Insight[] => {
    const insights: Insight[] = [];
    
    // Group meals by hour
    const mealsByHour = new Map<number, number>();
    meals.forEach(meal => {
      const hour = new Date(meal.created_at).getHours();
      mealsByHour.set(hour, (mealsByHour.get(hour) || 0) + 1);
    });

    // Find most common meal times
    const sortedHours = Array.from(mealsByHour.entries()).sort((a, b) => b[1] - a[1]);
    
    if (sortedHours.length > 0) {
      const mostCommonHour = sortedHours[0][0];
      const mealCount = sortedHours[0][1];
      
      if (mostCommonHour >= 22 || mostCommonHour <= 4) {
        insights.push({
          type: 'warning',
          title: '🌙 Late Night Eating Pattern',
          description: `${clientName} frequently logs meals late at night (${mostCommonHour}:00). This might affect sleep quality and metabolism.`,
          action: 'Suggest earlier meal timing',
          priority: 'medium',
          icon: '🌙'
        });
      }
      
      if (mealCount > meals.length * 0.4) {
        insights.push({
          type: 'trend',
          title: '⏰ Consistent Meal Timing',
          description: `${clientName} shows a consistent meal timing pattern around ${mostCommonHour}:00. This is great for routine building.`,
          priority: 'low',
          icon: '⏰'
        });
      }
    }

    return insights;
  };

  const analyzeMacroBalance = (meals: any[], dietGoals: any): Insight[] => {
    const insights: Insight[] = [];
    
    // Calculate average macro ratios
    const totalCalories = meals.reduce((sum, meal) => sum + (meal.calories || 0), 0);
    const totalProtein = meals.reduce((sum, meal) => sum + (meal.protein_g || 0), 0);
    const totalCarbs = meals.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0);
    const totalFat = meals.reduce((sum, meal) => sum + (meal.fat_g || 0), 0);

    if (totalCalories > 0) {
      const proteinRatio = (totalProtein * 4) / totalCalories * 100;
      const carbsRatio = (totalCarbs * 4) / totalCalories * 100;
      const fatRatio = (totalFat * 9) / totalCalories * 100;

      // Check for macro imbalances
      if (proteinRatio < 15) {
        insights.push({
          type: 'warning',
          title: '🥩 Low Protein Intake',
          description: `${clientName}'s protein intake is only ${proteinRatio.toFixed(1)}% of total calories. Consider suggesting more protein-rich foods.`,
          action: 'Recommend protein sources',
          priority: 'medium',
          icon: '🥩'
        });
      }

      if (carbsRatio > 70) {
        insights.push({
          type: 'warning',
          title: '🍞 High Carb Ratio',
          description: `${clientName}'s carb intake is ${carbsRatio.toFixed(1)}% of total calories. This might be too high for their goals.`,
          action: 'Review carb targets',
          priority: 'medium',
          icon: '🍞'
        });
      }
    }

    return insights;
  };

  const analyzeConsistency = (meals: any[]): Insight | null => {
    // Check for consistency in meal frequency
    const mealsByDate = new Map<string, number>();
    meals.forEach(meal => {
      const dateKey = new Date(meal.created_at).toISOString().slice(0, 10);
      mealsByDate.set(dateKey, (mealsByDate.get(dateKey) || 0) + 1);
    });

    const mealCounts = Array.from(mealsByDate.values());
    const avgMealsPerDay = mealCounts.reduce((sum, count) => sum + count, 0) / mealCounts.length;
    const variance = mealCounts.reduce((sum, count) => sum + Math.pow(count - avgMealsPerDay, 2), 0) / mealCounts.length;

    if (variance < 0.5 && avgMealsPerDay >= 2) {
      return {
        type: 'success',
        title: '📊 Excellent Consistency',
        description: `${clientName} shows very consistent meal frequency with an average of ${avgMealsPerDay.toFixed(1)} meals per day.`,
        priority: 'low',
        icon: '📊'
      };
    }

    return null;
  };

  const loadCoachNotes = async () => {
    try {
      const { data, error } = await supabase
        .from('coach_notes')
        .select('notes')
        .eq('telegram_user_id', selectedUserId)
        .single();

      if (error) {
        // Handle specific error cases
        if (error.code === 'PGRST116') {
          // No data found - this is normal for new clients
          setCoachNotes('');
        } else if (error.code === '406' || error.message?.includes('406')) {
          // Table doesn't exist or RLS issue - silently handle, no console output
          setCoachNotes('');
        } else {
          console.error('Error loading coach notes:', error);
          setCoachNotes('');
        }
      } else if (data) {
        setCoachNotes(data.notes || '');
      }
    } catch (error) {
      // Suppress all coach_notes errors to avoid console spam
      setCoachNotes('');
    }
  };

  const saveCoachNotes = async () => {
    try {
      setSavingNote(true);
      
      const { error } = await supabase
        .from('coach_notes')
        .upsert({
          telegram_user_id: selectedUserId,
          notes: coachNotes,
          updated_at: new Date().toISOString()
        });

      if (error) {
        if (error.code === '406' || error.message?.includes('406')) {
          alert('Coach notes feature is not available. Please ensure the coach_notes table exists in your database.');
        } else {
          throw error;
        }
        return;
      }
      
      // Show success feedback
      alert('Coach notes saved successfully! 📝');
    } catch (error) {
      console.error('Error saving coach notes:', error);
      alert('Error saving notes. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      // Match Trends colors: success=green, warning=yellow, action=blue, trend=neutral/blue
      case 'success': return 'border-[#8aff8a] bg-[#8aff8a]/10';
      case 'warning': return 'border-[#ffd700] bg-[#ffd700]/10';
      case 'action': return 'border-[#0eeaff] bg-[#0eeaff]/10';
      case 'trend': return 'border-[#8aff8a] bg-[#8aff8a]/10';
      default: return 'border-[#d4d4d4] bg-white';
    }
  };

  // All insights use the same yellow color for consistency
  const getCardColor = (type: string) => {
    return 'border-[#ffd700] bg-[#ffd700]/10'; // Yellow for all insights
  };

  // Quick Action Handlers
  const handleReviewGoals = async () => {
    // Send today's summary with suggestions
    const ok = await ReminderService.sendDailyGoalSummary(selectedUserId, clientName);
    alert(ok ? '✅ Sent today vs goals summary.' : '⚠️ Could not send summary.');
  };

  const handleSendMessage = async () => {
    const message = prompt(`Send a message to ${clientName}:\n\n(Leave empty to cancel)`);
    if (!message || !message.trim()) {
      return;
    }

    try {
      const sent = await TelegramService.sendMessage(selectedUserId, message.trim());
      if (sent) {
        alert(`✅ Message sent to ${clientName}.`);
      } else {
        alert('⚠️ Could not send message. Make sure the client has started the bot with /start.');
      }
    } catch (error) {
      console.error('Failed to send Telegram message:', error);
      alert('❌ Error sending message. Please try again.');
    }
  };

  const handleExportReport = async () => {
    const sent = await ReminderService.sendDailyVsWeeklySummary(selectedUserId, clientName);
    if (sent) {
      alert('✅ Sent today vs weekly average summary.');
      return;
    }

    // Fallback: download text report locally
    const reportData = {
      clientName,
      insights: insights.map(insight => ({
        title: insight.title,
        description: insight.description,
        priority: insight.priority
      })),
      coachNotes,
      generatedAt: new Date().toLocaleString()
    };

    // Create a simple text report (could be enhanced to PDF)
    const reportText = `
CLIENT PROGRESS REPORT
=====================
Client: ${clientName}
Generated: ${reportData.generatedAt}

INSIGHTS:
${insights.map(insight => `• ${insight.title}: ${insight.description}`).join('\n')}

COACH NOTES:
${coachNotes || 'No notes available'}

=====================
Generated by MacTrack Dashboard
    `.trim();

    // Create and download the report
    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clientName}_Progress_Report_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    alert(`Progress summary for ${clientName} downloaded! 📊`);
  };

  const handleRefreshData = async () => {
    setLoading(true);
    await generateInsights();
    await loadCoachNotes();
    alert('Data refreshed successfully! 🔄');
  };

  // Pull-to-refresh handler
  const handlePullToRefresh = async () => {
    setLoading(true);
    try {
      await generateInsights();
      await loadCoachNotes();
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="retro-card">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🧠 Goal & Performance Insights</h3>
        </div>
        <PullToRefresh onRefresh={handlePullToRefresh} disabled={loading}>
          <div className="p-4 md:p-6">
            <div className="text-center py-8 text-[#2d2d2d]">Analyzing performance patterns...</div>
          </div>
        </PullToRefresh>
      </div>
    );
  }

  return (
    <div className="retro-card">
      <div className="index-tab">
        <h3 className="text-lg font-semibold text-[#1f1f1f]">🧠 Goal & Performance Insights</h3>
      </div>
      
      <PullToRefresh onRefresh={handlePullToRefresh} disabled={loading}>
        <div>
          {/* Tab Navigation */}
          <div className="border-b border-[#d4d4d4]">
            <div className="flex space-x-1 p-4 pb-0">
              <button
                onClick={() => setActiveTab('insights')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  activeTab === 'insights'
                    ? 'bg-[#0eeaff] text-[#1f1f1f] border-b-2 border-[#0eeaff]'
                    : 'bg-[#f8f4e6] text-[#666] hover:bg-[#e6f3ff]'
                }`}
              >
                📊 Basic Insights
              </button>
              <button
                onClick={() => setActiveTab('meal-analysis')}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                  activeTab === 'meal-analysis'
                    ? 'bg-[#0eeaff] text-[#1f1f1f] border-b-2 border-[#0eeaff]'
                    : 'bg-[#f8f4e6] text-[#666] hover:bg-[#e6f3ff]'
                }`}
              >
                🔍 Advanced Meal Analysis
              </button>
            </div>
          </div>
          
          <div className="p-4 md:p-6">
            {/* Tab Content */}
            {activeTab === 'insights' && (
              <>
                {/* Client Header */}
                <div className="mb-6 p-4 bg-gradient-to-r from-neon-blue to-neon-green bg-opacity-10 rounded-lg border border-neon-blue">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-neon-blue to-neon-green rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {clientName.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-text-dark">{clientName}</h2>
                      <p className="text-sm text-text-medium">Performance Insights & Analysis</p>
                    </div>
                  </div>
                </div>

                {/* Insights Section */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold text-[#1f1f1f] mb-4">📊 AI-Generated Insights</h4>
                  
                  {insights.length === 0 ? (
                    <div className="text-center py-8 text-[#2d2d2d]">
                      No insights available yet. More data will generate personalized recommendations.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {insights.map((insight, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border-2 ${getCardColor(insight.type)}`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="text-2xl">{insight.icon}</div>
                            <div className="flex-1">
                              <h5 className="font-semibold text-[#1f1f1f] mb-1">{insight.title}</h5>
                              <p className="text-sm text-[#2d2d2d] mb-2">{insight.description}</p>
                              {insight.action && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-[#666]">Suggested Action:</span>
                                  <span className="text-xs font-medium text-[#0eeaff]">{insight.action}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Coach Notes Section */}
                <div className="border-t border-[#d4d4d4] pt-6">
                  <h4 className="text-md font-semibold text-[#1f1f1f] mb-4">📝 Coach Notes</h4>
                  <div className="space-y-3">
                    <textarea
                      value={coachNotes}
                      onChange={(e) => setCoachNotes(e.target.value)}
                      placeholder="Add your coaching notes, observations, or action items for this client..."
                      className="vintage-input w-full h-32 resize-none"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={saveCoachNotes}
                        disabled={savingNote}
                        className={`neon-button px-4 py-2 text-sm ${
                          savingNote ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                        }`}
                      >
                        {savingNote ? '💾 Saving...' : '💾 Save Notes'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="border-t border-[#d4d4d4] pt-6 mt-6">
                  <h4 className="text-md font-semibold text-[#1f1f1f] mb-4">⚡ Quick Actions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button 
                      onClick={handleReviewGoals}
                      className="bg-[#8aff8a]/10 border border-[#8aff8a] rounded-lg p-3 text-left hover:bg-[#8aff8a]/20 transition-colors cursor-pointer"
                    >
                      <div className="text-lg mb-1">🎯</div>
                      <div className="text-sm font-medium text-[#1f1f1f]">Send Today vs Goals</div>
                      <div className="text-xs text-[#666]">Deliver daily summary with suggestions</div>
                    </button>
                    <button 
                      onClick={handleSendMessage}
                      className="bg-[#0eeaff]/10 border border-[#0eeaff] rounded-lg p-3 text-left hover:bg-[#0eeaff]/20 transition-colors cursor-pointer"
                    >
                      <div className="text-lg mb-1">📱</div>
                      <div className="text-sm font-medium text-[#1f1f1f]">Send Message</div>
                      <div className="text-xs text-[#666]">Reach out to client</div>
                    </button>
                    <button 
                      onClick={handleExportReport}
                      className="bg-[#ffd700]/10 border border-[#ffd700] rounded-lg p-3 text-left hover:bg-[#ffd700]/20 transition-colors cursor-pointer"
                    >
                      <div className="text-lg mb-1">📊</div>
                      <div className="text-sm font-medium text-[#1f1f1f]">Send Today vs Weekly</div>
                      <div className="text-xs text-[#666]">Message daily vs weekly average</div>
                    </button>
                    <button 
                      onClick={handleRefreshData}
                      className="bg-[#ff6b6b]/10 border border-[#ff6b6b] rounded-lg p-3 text-left hover:bg-[#ff6b6b]/20 transition-colors cursor-pointer"
                    >
                      <div className="text-lg mb-1">🔄</div>
                      <div className="text-sm font-medium text-[#1f1f1f]">Refresh Data</div>
                      <div className="text-xs text-[#666]">Update insights</div>
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'meal-analysis' && (
              <AdvancedMealAnalysis 
                selectedUserId={selectedUserId} 
                clientName={clientName}
                onUpgrade={onUpgrade}
              />
            )}
          </div>
        </div>
      </PullToRefresh>
    </div>
  );
} 